import { useState, useMemo, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormStepIndicator } from "@/components/ui/form-step-indicator";
import { Table as TableComponent, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Upload, FileText, AlertTriangle, Check, Loader2 } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEstablishment } from "@/contexts/EstablishmentContext";
import { parseCSV, autoDetectMapping, mapRowToClient, TARGET_FIELDS, type ParsedCSVResult } from "@/lib/csvParseUtils";

interface ImportClientsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  existingEmails: Set<string>;
  onImportComplete: () => void;
}

const STEP_TITLES = ["Fichier CSV", "Mapping colonnes", "Aperçu", "Confirmation"];

export function ImportClientsDialog({ open, onOpenChange, existingEmails, onImportComplete }: ImportClientsDialogProps) {
  const { establishmentId } = useEstablishment();
  const [step, setStep] = useState(1);
  const [csvData, setCsvData] = useState<ParsedCSVResult | null>(null);
  const [fileName, setFileName] = useState("");
  const [mapping, setMapping] = useState<Record<number, string>>({});
  const [importMode, setImportMode] = useState<"create_only" | "create_and_update">("create_only");
  const [isImporting, setIsImporting] = useState(false);

  const resetState = useCallback(() => {
    setStep(1);
    setCsvData(null);
    setFileName("");
    setMapping({});
    setImportMode("create_only");
    setIsImporting(false);
  }, []);

  const handleClose = useCallback(() => {
    resetState();
    onOpenChange(false);
  }, [resetState, onOpenChange]);

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      toast({ title: "Fichier trop volumineux", description: "La taille maximale est de 5 Mo.", variant: "destructive" });
      return;
    }

    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const parsed = parseCSV(text);
      
      if (parsed.headers.length === 0) {
        toast({ title: "Fichier vide", description: "Le fichier CSV ne contient aucune donnée.", variant: "destructive" });
        return;
      }
      
      setCsvData(parsed);
      const autoMapping = autoDetectMapping(parsed.headers);
      setMapping(autoMapping);
      setStep(2);
    };
    reader.readAsText(file);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.name.endsWith('.txt'))) {
      const input = document.createElement('input');
      input.type = 'file';
      const dt = new DataTransfer();
      dt.items.add(file);
      input.files = dt.files;
      handleFileUpload({ target: input } as any);
    }
  }, [handleFileUpload]);

  // Mapped clients for preview
  const mappedClients = useMemo(() => {
    if (!csvData) return [];
    return csvData.rows.map(row => mapRowToClient(row, mapping)).filter(Boolean) as Record<string, string>[];
  }, [csvData, mapping]);

  const duplicateCount = useMemo(() => {
    return mappedClients.filter(c => existingEmails.has(c.email)).length;
  }, [mappedClients, existingEmails]);

  const newCount = useMemo(() => {
    return mappedClients.length - duplicateCount;
  }, [mappedClients, duplicateCount]);

  const invalidCount = useMemo(() => {
    if (!csvData) return 0;
    return csvData.totalRows - mappedClients.length;
  }, [csvData, mappedClients]);

  const hasEmailMapping = useMemo(() => {
    return Object.values(mapping).includes('email');
  }, [mapping]);

  const handleImport = async () => {
    if (!establishmentId || mappedClients.length === 0) return;
    
    setIsImporting(true);
    try {
      const { data, error } = await supabase.functions.invoke('import-clients', {
        body: {
          clients: mappedClients,
          establishmentId,
          mode: importMode,
        },
      });

      if (error) {
        toast({ title: "Erreur d'import", description: "L'import a échoué. Veuillez réessayer.", variant: "destructive" });
        return;
      }

      toast({
        title: "Import terminé",
        description: `${data.created} créé(s), ${data.updated} mis à jour, ${data.skipped} ignoré(s)`,
      });

      onImportComplete();
      handleClose();
    } catch (err) {
      console.error('Import error:', err);
      toast({ title: "Erreur", description: "Une erreur inattendue s'est produite", variant: "destructive" });
    } finally {
      setIsImporting(false);
    }
  };

  const updateMapping = (index: number, field: string) => {
    setMapping(prev => {
      const next = { ...prev };
      if (field === '') {
        delete next[index];
      } else {
        // Remove any existing mapping to this field
        for (const [key, val] of Object.entries(next)) {
          if (val === field && parseInt(key) !== index) {
            delete next[parseInt(key)];
          }
        }
        next[index] = field;
      }
      return next;
    });
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) handleClose(); else onOpenChange(v); }}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Importer des clients</DialogTitle>
          <DialogDescription>
            Importez vos clients depuis un fichier CSV (Excel, Aleno, TheFork, etc.)
          </DialogDescription>
        </DialogHeader>

        <FormStepIndicator currentStep={step} totalSteps={4} stepTitles={STEP_TITLES} className="mb-4" />

        {/* Step 1: File Upload */}
        {step === 1 && (
          <div
            className="border-2 border-dashed border-muted-foreground/25 rounded-lg p-8 text-center hover:border-primary/50 transition-colors cursor-pointer"
            onDragOver={(e) => e.preventDefault()}
            onDrop={handleDrop}
            onClick={() => document.getElementById('csv-file-input')?.click()}
          >
            <Upload className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
            <p className="text-sm text-muted-foreground mb-2">
              Glissez-déposez votre fichier CSV ici, ou cliquez pour sélectionner
            </p>
            <p className="text-xs text-muted-foreground">
              Formats acceptés : .csv, .txt — Max 5 Mo
            </p>
            <Input
              id="csv-file-input"
              type="file"
              accept=".csv,.txt"
              className="hidden"
              onChange={handleFileUpload}
            />
          </div>
        )}

        {/* Step 2: Column Mapping */}
        {step === 2 && csvData && (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <FileText className="h-4 w-4" />
              <span>{fileName} — {csvData.totalRows} lignes, séparateur « {csvData.separator === '\t' ? 'TAB' : csvData.separator} »</span>
            </div>

            <div className="space-y-2 max-h-[40vh] overflow-y-auto">
              {csvData.headers.map((header, index) => {
                const preview = csvData.rows.slice(0, 3).map(r => r[index]).filter(Boolean).join(', ');
                return (
                  <div key={index} className="flex items-center gap-3 p-2 rounded border bg-muted/30">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{header}</p>
                      <p className="text-xs text-muted-foreground truncate">{preview || '—'}</p>
                    </div>
                    <Select
                      value={mapping[index] || '__ignore'}
                      onValueChange={(v) => updateMapping(index, v === '__ignore' ? '' : v)}
                    >
                      <SelectTrigger className="w-[180px]">
                        <SelectValue placeholder="Ignorer" />
                      </SelectTrigger>
                      <SelectContent>
                        {TARGET_FIELDS.map(f => (
                          <SelectItem key={f.value || '__ignore'} value={f.value || '__ignore'}>
                            {f.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                );
              })}
            </div>

            {!hasEmailMapping && (
              <div className="flex items-center gap-2 p-3 rounded bg-destructive/10 text-destructive text-sm">
                <AlertTriangle className="h-4 w-4 shrink-0" />
                <span>La colonne « Email » est obligatoire pour l'import.</span>
              </div>
            )}
          </div>
        )}

        {/* Step 3: Preview */}
        {step === 3 && csvData && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 rounded bg-green-500/10 text-center">
                <p className="text-2xl font-bold text-green-600">{newCount}</p>
                <p className="text-xs text-muted-foreground">Nouveaux</p>
              </div>
              <div className="p-3 rounded bg-amber-500/10 text-center">
                <p className="text-2xl font-bold text-amber-600">{duplicateCount}</p>
                <p className="text-xs text-muted-foreground">Doublons</p>
              </div>
              <div className="p-3 rounded bg-muted text-center">
                <p className="text-2xl font-bold text-muted-foreground">{invalidCount}</p>
                <p className="text-xs text-muted-foreground">Ignorés (sans email)</p>
              </div>
            </div>

            {/* Preview table */}
            <div className="rounded border overflow-x-auto max-h-[30vh]">
              <TableComponent>
                <TableHeader>
                  <TableRow>
                    <TableHead>Email</TableHead>
                    <TableHead>Prénom</TableHead>
                    <TableHead>Nom</TableHead>
                    <TableHead>Téléphone</TableHead>
                    <TableHead>Statut</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mappedClients.slice(0, 10).map((client, i) => {
                    const isDuplicate = existingEmails.has(client.email);
                    return (
                      <TableRow key={i}>
                        <TableCell className="text-xs">{client.email}</TableCell>
                        <TableCell className="text-xs">{client.firstName || '—'}</TableCell>
                        <TableCell className="text-xs">{client.lastName || '—'}</TableCell>
                        <TableCell className="text-xs">{client.phone || '—'}</TableCell>
                        <TableCell>
                          {isDuplicate ? (
                            <Badge variant="outline" className="text-xs">Doublon</Badge>
                          ) : (
                            <Badge className="text-xs bg-green-600">Nouveau</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </TableComponent>
            </div>
            {mappedClients.length > 10 && (
              <p className="text-xs text-muted-foreground text-center">
                ... et {mappedClients.length - 10} autres lignes
              </p>
            )}
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && (
          <div className="space-y-4">
            <RadioGroup value={importMode} onValueChange={(v) => setImportMode(v as any)} className="space-y-3">
              <div className="flex items-start space-x-3 p-3 rounded border">
                <RadioGroupItem value="create_only" id="create_only" className="mt-0.5" />
                <Label htmlFor="create_only" className="cursor-pointer">
                  <p className="font-medium">Créer uniquement les nouveaux</p>
                  <p className="text-xs text-muted-foreground">Les {duplicateCount} doublons seront ignorés</p>
                </Label>
              </div>
              <div className="flex items-start space-x-3 p-3 rounded border">
                <RadioGroupItem value="create_and_update" id="create_and_update" className="mt-0.5" />
                <Label htmlFor="create_and_update" className="cursor-pointer">
                  <p className="font-medium">Créer et mettre à jour</p>
                  <p className="text-xs text-muted-foreground">Les {duplicateCount} doublons seront mis à jour avec les nouvelles données</p>
                </Label>
              </div>
            </RadioGroup>

            <div className="p-3 rounded bg-muted text-sm">
              <p><strong>{newCount}</strong> client(s) seront créés</p>
              {importMode === "create_and_update" && duplicateCount > 0 && (
                <p><strong>{duplicateCount}</strong> client(s) seront mis à jour</p>
              )}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 1 && (
            <Button variant="outline" onClick={() => setStep(s => s - 1)} disabled={isImporting}>
              Précédent
            </Button>
          )}
          {step === 1 && (
            <Button variant="outline" onClick={handleClose}>
              Annuler
            </Button>
          )}
          {step === 2 && (
            <Button onClick={() => setStep(3)} disabled={!hasEmailMapping}>
              Suivant
            </Button>
          )}
          {step === 3 && (
            <Button onClick={() => setStep(4)} disabled={mappedClients.length === 0}>
              Suivant
            </Button>
          )}
          {step === 4 && (
            <Button onClick={handleImport} disabled={isImporting || mappedClients.length === 0}>
              {isImporting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Import en cours...
                </>
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Importer {newCount + (importMode === "create_and_update" ? duplicateCount : 0)} client(s)
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
